<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Lock, User, Van } from '@element-plus/icons-vue'
import type { FormInstance, FormRules } from 'element-plus'
import { ElMessage } from 'element-plus'

import { ApiClientError } from '@/api/client'
import IcpFilingLink from '@/components/IcpFilingLink.vue'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const formRef = ref<FormInstance>()
const submitting = ref(false)
const form = reactive({ username: '', password: '' })

const rules: FormRules = {
  username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
}

async function submit(): Promise<void> {
  const valid = await formRef.value?.validate().catch(() => false)
  if (!valid) return
  submitting.value = true
  try {
    const user = await auth.login(form.username, form.password)
    const requested = typeof route.query.redirect === 'string' ? route.query.redirect : null
    await router.replace(user.mustChangePassword ? '/change-password' : (requested ?? user.defaultRoute))
    ElMessage.success(`欢迎回来，${user.fullName}`)
  } catch (error) {
    ElMessage.error(error instanceof ApiClientError ? error.message : '暂时无法登录，请稍后重试')
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <main class="login-page">
    <section class="login-visual">
      <div class="login-brand">
        <span class="brand-mark brand-mark-large"><Van /></span>
        <span><strong>徐州港华</strong><small>企业车辆管理系统</small></span>
      </div>
      <div class="login-hero-copy">
        <p class="eyebrow">FLEET OPERATIONS</p>
        <h1>车辆全生命周期数据<br />清晰、可靠、可追溯</h1>
        <p>统一管理车辆档案、补能、维修保养和保险，让日常台账与经营数据保持一致。</p>
      </div>
      <div class="login-feature-row">
        <span><strong>4</strong><small>核心业务域</small></span>
        <span><strong>PC + H5</strong><small>响应式适配</small></span>
        <span><strong>全程</strong><small>操作审计</small></span>
      </div>
      <div class="visual-orbit orbit-one" />
      <div class="visual-orbit orbit-two" />
    </section>

    <section class="login-panel">
      <div class="mobile-login-brand">
        <span class="brand-mark"><Van /></span>
        <span><strong>徐州港华</strong><small>车辆管理系统</small></span>
      </div>
      <div class="login-card">
        <header>
          <p>欢迎使用</p>
          <h2>登录车辆管理系统</h2>
          <span>请使用企业内部账号登录</span>
        </header>
        <el-form ref="formRef" :model="form" :rules="rules" label-position="top" @submit.prevent="submit">
          <el-form-item label="用户名" prop="username">
            <el-input v-model="form.username" size="large" autocomplete="username" placeholder="请输入用户名" :prefix-icon="User" />
          </el-form-item>
          <el-form-item label="密码" prop="password">
            <el-input
              v-model="form.password"
              size="large"
              type="password"
              autocomplete="current-password"
              show-password
              placeholder="请输入密码"
              :prefix-icon="Lock"
              @keyup.enter="submit"
            />
          </el-form-item>
          <div class="login-help">连续登录失败 5 次后，账号将锁定 15 分钟</div>
          <el-button type="primary" size="large" native-type="submit" :loading="submitting" class="login-submit">
            登录系统
          </el-button>
        </el-form>
      </div>
      <footer class="login-footer">
        <span>仅限徐州港华内部授权人员使用</span>
        <IcpFilingLink />
      </footer>
    </section>
  </main>
</template>
